import React, { createContext, useContext, useEffect, useState } from "react";
import { api, warmOfflineCache } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("pms_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("pms_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("pms_token", r.data.token);
    setUser(r.data.user);
    // Warm the offline cache so the app remains fully usable if the network drops.
    // Fetches properties + settings + POS menu + guests + housekeeping snapshots.
    api.get("/properties").then((p) => warmOfflineCache(p.data || [])).catch(() => {});
    return r.data.user;
  };

  const loginWithGoogleSession = async (session_id) => {
    const r = await api.post("/auth/google-session", { session_id });
    localStorage.setItem("pms_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (payload) => {
    await api.post("/auth/register", payload);
    return login(payload.email, payload.password);
  };

  const logout = () => {
    localStorage.removeItem("pms_token");
    localStorage.removeItem("pms_active_property");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, loginWithGoogleSession, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Convenience hook — true/false whether the current user is the master owner.
export function useIsMaster() {
  const [isMaster, setIsMaster] = useState(false);
  const { user } = useAuth() || {};
  useEffect(() => {
    if (!user) { setIsMaster(false); return; }
    api.get("/platform/master", { silent: true }).then((r) => setIsMaster(!!r.data?.is_master)).catch(() => setIsMaster(false));
  }, [user?.id]);
  return isMaster;
}

